import cn from 'classnames'
import Image from 'next/image'
import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {CloseIcon} from '@/components/common/icons/CloseIcon'
import {Trans} from '@/i18n'
import {SIDE_BANNER_TRANSITION} from '@/theme/cssTransitions'
import {IconSize} from '@/theme/icons'
import {
  FORM_CONTAINER_PADDING,
  FORM_CONTAINER_PADDING_MOBILE,
  HEADER_HEIGHT_MOBILE,
} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

type PopupCardProps = {
  title: string
  description: string
  imageSrc: string
  imageAlt: string
  imageBackgroundColor?: string
  isImageCentered?: boolean
  buttonText?: string
  buttonOnClick?: () => void
  isVisible: boolean
  onClose: () => void
}

export const PopupCard: FC<React.PropsWithChildren<PopupCardProps>> = ({
  title,
  description,
  imageSrc,
  imageAlt,
  imageBackgroundColor = theme.palette.primary.main,
  isImageCentered = false,
  buttonText,
  buttonOnClick,
  isVisible,
  onClose,
}) => (
  <>
    <div className={cn('wrapper', isVisible && 'visible')}>
      <div className="image-wrapper">
        <div className={cn('image', isImageCentered && 'centered')}>
          <Image
            src={imageSrc}
            alt={imageAlt}
            style={{objectFit: 'contain'}}
            width={56}
            height={112}
          />
        </div>
      </div>
      <div className="content">
        <div className="header">
          <Text variant={TextVariant.text14} fontWeight={TextFontWeight.bold}>
            {title}
          </Text>
          <button className="close" type="button" onClick={() => onClose()}>
            <CloseIcon size={IconSize.medium} color={theme.palette.gray[600]} />
          </button>
        </div>
        <div className="description">
          <Text variant={TextVariant.text14} color={theme.palette.text.light}>
            <Trans i18nKey={description} />
          </Text>
        </div>
        {buttonText && (
          <div className="button-wrapper">
            <Button
              type="button"
              onClick={buttonOnClick}
              variant={ButtonVariant.primary}
              size={ButtonSize.small}
              fullWidth
            >
              {buttonText}
            </Button>
          </div>
        )}
      </div>
    </div>

    <style jsx>{`
      .wrapper {
        display: flex;
        position: fixed;
        width: 100%;
        max-width: 360px;
        right: -360px;
        bottom: ${FORM_CONTAINER_PADDING};
        border-radius: 12px;
        background-color: ${theme.palette.background.default};
        box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.1),
          0px 4px 6px -2px rgba(16, 24, 40, 0.05);
        z-index: 4;
        transition: right ${SIDE_BANNER_TRANSITION}ms ease-in-out;
      }

      .visible {
        right: ${FORM_CONTAINER_PADDING};
        transition: right ${SIDE_BANNER_TRANSITION}ms 1000ms ease-in-out;
      }

      .image-wrapper {
        display: flex;
        width: 80px;
        flex: 0 0 80px;
        border-top-left-radius: 12px;
        border-bottom-left-radius: 12px;
        background-color: ${imageBackgroundColor};
        z-index: 1;
      }

      .image {
        position: relative;
        width: 56px;
        height: 112px;
        margin: 0 auto;
      }

      .centered {
        margin: auto;
      }

      .content {
        padding: ${theme.spacing[3]};
        border: 1px solid ${theme.palette.gray[200]};
        border-left: 0;
        border-top-right-radius: 12px;
        border-bottom-right-radius: 12px;
      }

      .header {
        display: flex;
        justify-content: space-between;
        gap: ${theme.spacing[3]};
      }

      .description {
        margin-top: ${theme.spacing[2]};
      }

      .close {
        display: flex;
        padding: ${theme.spacing[0.5]};
        margin: -${theme.spacing[0.5]};
      }

      .button-wrapper {
        margin-top: ${theme.spacing[3]};
      }

      @media ${TABLET_SCREENSIZE} {
        .wrapper {
          bottom: calc(${HEADER_HEIGHT_MOBILE} + ${theme.spacing[2.5]});
        }
        .visible {
          right: 64px;
        }
      }

      @media ${MOBILE_SCREENSIZE} {
        .wrapper {
          max-width: calc(100% - ${FORM_CONTAINER_PADDING_MOBILE} * 2);
          right: calc(-100% - ${FORM_CONTAINER_PADDING_MOBILE} * 2);
        }

        .visible {
          right: ${FORM_CONTAINER_PADDING_MOBILE};
        }
      }
    `}</style>
  </>
)
